import React from 'react';
import { FaHotel, FaHome, FaUtensils, FaMountain, FaLightbulb, FaCalendarCheck, FaMoneyBillWave, FaMapMarkedAlt, FaStar, FaHandsHelping, FaSearch, FaGift, FaShoePrints, FaHands, FaLanguage, FaHandHoldingUsd, FaEnvelope, FaPhone, FaInstagram, FaTelegram, FaTwitter, FaFacebook } from 'react-icons/fa';

const persianPattern = {
  backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHBhdHRlcm5UcmFuc2Zvcm09InJvdGF0ZSg0NSkiPjxyZWN0IHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0iI2ZmYzEwNyI+PC9yZWN0PjxyZWN0IHg9IjIwIiB5PSIwIiB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIGZpbGw9IiNmZmMxMDciPjwvcmVjdD48cmVjdCB4PSIwIiB5PSIyMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjZmZjMTA3Ij48L3JlY3Q+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIj48L3JlY3Q+PC9zdmc+')`,
  opacity: 0.05,
  position: 'absolute' as const,
  inset: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
};

const StayGuide: React.FC = () => (
  <div className="bg-gray-50 font-sans" dir="ltr">
    {/* Header with Persian pattern */}
    <header className="relative bg-amber-600 text-white overflow-hidden">
      <div style={persianPattern}></div>
      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-8 md:mb-0">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Staying in Iran</h1>
            <h2 className="text-2xl md:text-3xl font-semibold mb-6">Hotels & Local Hosts</h2>
            <p className="text-lg mb-8">Discover the warmth of Persian hospitality through traditional hotels and authentic local homestays.</p>
            <div className="flex space-x-4">
              <a href="#hotels" className="bg-white text-amber-700 px-6 py-3 rounded-lg font-medium hover:bg-amber-50 transition">Hotels</a>
              <a href="#local-hosts" className="border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-amber-700 transition">Local Hosts</a>
            </div>
          </div>
          <div className="md:w-1/2">
            <img src="https://images.unsplash.com/photo-1548588681-adf41d474533?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" alt="Traditional Persian architecture" className="rounded-lg shadow-xl w-full h-auto max-h-96 object-cover" />
          </div>
        </div>
      </div>
    </header>

    {/* Introduction Section */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto">
          <h3 className="text-2xl font-semibold text-amber-700 mb-4">Experience Persian Hospitality</h3>
          <p className="text-gray-700 mb-8">
            Iran offers a diverse range of accommodation options, from luxurious hotels in bustling cities to cozy traditional guesthouses and homestays where you can experience genuine Persian hospitality. Whether you prefer modern comforts or authentic cultural immersion, Iran has something for every traveler.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center bg-amber-50 px-4 py-2 rounded-full"><FaHotel className="text-amber-600 mr-2" /> <span>5-star hotels</span></div>
            <div className="flex items-center bg-amber-50 px-4 py-2 rounded-full"><FaHome className="text-amber-600 mr-2" /> <span>Traditional guesthouses</span></div>
            <div className="flex items-center bg-amber-50 px-4 py-2 rounded-full"><FaUtensils className="text-amber-600 mr-2" /> <span>Homestays with meals</span></div>
            <div className="flex items-center bg-amber-50 px-4 py-2 rounded-full"><FaMountain className="text-amber-600 mr-2" /> <span>Eco-lodges</span></div>
          </div>
        </div>
      </div>
    </section>

    {/* Hotels Section */}
    <section id="hotels" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Hotels in Iran</h2>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {/* Hotel Card 1 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-xl">
            <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" alt="Luxury hotel in Tehran" className="w-full h-48 object-cover" />
            <div className="p-6">
              <div className="flex items-center mb-2">
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded">5-star</span>
                <span className="text-gray-500 text-sm ml-2">Tehran</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Espinas Palace Hotel</h3>
              <p className="text-gray-600 mb-4">One of Tehran's most luxurious hotels with modern amenities and excellent service.</p>
              <div className="flex justify-between items-center">
                <span className="font-bold text-amber-700">From $120/night</span>
                <button className="text-amber-600 hover:text-amber-800 font-medium">View details →</button>
              </div>
            </div>
          </div>
          {/* Hotel Card 2 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-xl">
            <img src="https://images.unsplash.com/photo-1582719471385-7ad3395e5b02?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80" alt="Traditional hotel in Isfahan" className="w-full h-48 object-cover" />
            <div className="p-6">
              <div className="flex items-center mb-2">
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded">Boutique</span>
                <span className="text-gray-500 text-sm ml-2">Isfahan</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Abbasi Hotel</h3>
              <p className="text-gray-600 mb-4">A historic caravanserai converted into a stunning traditional hotel with Persian architecture.</p>
              <div className="flex justify-between items-center">
                <span className="font-bold text-amber-700">From $80/night</span>
                <button className="text-amber-600 hover:text-amber-800 font-medium">View details →</button>
              </div>
            </div>
          </div>
          {/* Hotel Card 3 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden transition duration-300 hover:-translate-y-1 hover:shadow-xl">
            <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" alt="Yazd traditional hotel" className="w-full h-48 object-cover" />
            <div className="p-6">
              <div className="flex items-center mb-2">
                <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-0.5 rounded">Heritage</span>
                <span className="text-gray-500 text-sm ml-2">Yazd</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Moshir al-Mamalek Garden Hotel</h3>
              <p className="text-gray-600 mb-4">A beautiful traditional hotel set in a historic Persian garden with wind towers.</p>
              <div className="flex justify-between items-center">
                <span className="font-bold text-amber-700">From $65/night</span>
                <button className="text-amber-600 hover:text-amber-800 font-medium">View details →</button>
              </div>
            </div>
          </div>
        </div>
        {/* Hotel Booking Tips */}
        <div className="bg-white rounded-xl shadow-md p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-semibold mb-6 text-amber-700 flex items-center">
            <FaLightbulb className="mr-3" /> Tips for Booking Hotels in Iran
          </h3>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="bg-amber-100 p-3 rounded-full mr-4">
                <FaCalendarCheck className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Book in Advance</h4>
                <p className="text-gray-600">Popular hotels, especially during Nowruz (Persian New Year in March) and other holidays, get booked quickly. Reserve at least 2-3 months ahead for peak seasons.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-amber-100 p-3 rounded-full mr-4">
                <FaMoneyBillWave className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Payment Methods</h4>
                <p className="text-gray-600">International credit cards often don't work due to sanctions. Many hotels accept payment in cash (Euros or USD) or through international money transfers.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-amber-100 p-3 rounded-full mr-4">
                <FaMapMarkedAlt className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Location Matters</h4>
                <p className="text-gray-600">In big cities like Tehran, choose hotels near metro stations for easy transportation. In historical cities like Isfahan, staying near Naqsh-e Jahan Square is ideal.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-amber-100 p-3 rounded-full mr-4">
                <FaStar className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Star Ratings</h4>
                <p className="text-gray-600">Iranian hotel ratings may differ from international standards. A 4-star hotel in Iran might be comparable to a 3-star hotel elsewhere. Read recent reviews.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Local Hosts Section */}
    <section id="local-hosts" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Staying with Local Hosts</h2>
        <div className="flex flex-col md:flex-row gap-8 mb-12">
          <div className="md:w-1/2">
            <img src="https://images.unsplash.com/photo-1600585152220-90363fe7e115?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80" alt="Iranian family hosting guests" className="rounded-lg shadow-md w-full h-auto" />
          </div>
          <div className="md:w-1/2">
            <h3 className="text-2xl font-semibold mb-4 text-amber-700">Experience Authentic Persian Hospitality</h3>
            <p className="text-gray-600 mb-6">
              Staying with local hosts is one of the best ways to experience genuine Iranian culture and hospitality. Many families open their homes to travelers, offering comfortable rooms and often delicious homemade meals.
            </p>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="bg-amber-100 p-2 rounded-full mr-3 mt-1">
                  <FaHome className="text-amber-700 text-sm" />
                </div>
                <div>
                  <h4 className="font-semibold">Types of Local Accommodation</h4>
                  <p className="text-gray-600 text-sm">Traditional guesthouses (Mosaferkhaneh), homestays, eco-lodges, and rural stays in villages.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-amber-100 p-2 rounded-full mr-3 mt-1">
                  <FaUtensils className="text-amber-700 text-sm" />
                </div>
                <div>
                  <h4 className="font-semibold">Meals Included</h4>
                  <p className="text-gray-600 text-sm">Many hosts offer breakfast and sometimes dinner featuring authentic Persian cuisine.</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-amber-100 p-2 rounded-full mr-3 mt-1">
                  <FaHandsHelping className="text-amber-700 text-sm" />
                </div>
                <div>
                  <h4 className="font-semibold">Cultural Exchange</h4>
                  <p className="text-gray-600 text-sm">Opportunities to learn Farsi phrases, participate in family activities, and gain local insights.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Local Hosts Tips */}
        <div className="bg-amber-50 rounded-xl shadow-md p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-semibold mb-6 text-amber-700 flex items-center">
            <FaHandsHelping className="mr-3" /> Tips for Staying with Local Hosts
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaSearch className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Finding Hosts</h4>
                <p className="text-gray-600 text-sm">Use platforms like Couchsurfing, local homestay networks, or ask for recommendations from other travelers.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaGift className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Bring Small Gifts</h4>
                <p className="text-gray-600 text-sm">It's customary to bring a small gift from your country when staying with a host - sweets or souvenirs are appreciated.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaShoePrints className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Shoes Off</h4>
                <p className="text-gray-600 text-sm">Always remove your shoes when entering an Iranian home. Some hosts may provide slippers.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaHands className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Cultural Sensitivity</h4>
                <p className="text-gray-600 text-sm">Dress modestly in homes, especially when common areas are shared with family members.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaLanguage className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Language Barrier</h4>
                <p className="text-gray-600 text-sm">Many younger Iranians speak English, but learning basic Farsi phrases will enhance your experience.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="bg-white p-3 rounded-full mr-4 shadow">
                <FaHandHoldingUsd className="text-amber-700" />
              </div>
              <div>
                <h4 className="font-semibold mb-1">Payment</h4>
                <p className="text-gray-600 text-sm">For paid homestays, clarify payment expectations upfront. Cash in local currency is usually preferred.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Accommodation Vocabulary */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Essential Accommodation Vocabulary</h2>
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {/* Vocabulary Cards */}
            {[
              { farsi: 'هتل (Hotel)', desc: 'Hotel - Pronounced "hotel" (same as English)' },
              { farsi: 'مسافرخانه (Mosaferkhaneh)', desc: 'Traditional guesthouse or inn' },
              { farsi: 'اتاق (Otāgh)', desc: 'Room - Pronounced "o-tagh"' },
              { farsi: 'صبحانه (Sobhāneh)', desc: 'Breakfast - Pronounced "sobhāneh"' },
              { farsi: 'رزرو کردن (Rezerv kardan)', desc: 'To reserve/book - Pronounced "rezerv kardan"' },
              { farsi: 'کلید (Kelid)', desc: 'Key - Pronounced "kelid"' },
              { farsi: 'حمام (Hammām)', desc: 'Bathroom - Pronounced "hamām"' },
              { farsi: 'اینترنت (Internet)', desc: 'Internet - Pronounced same as English' },
              { farsi: 'چند شب؟ (Chand shab?)', desc: 'How many nights? - Pronounced "chand shab?"' },
            ].map((v, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow-sm hover:bg-amber-50 transition">
                <div className="flex items-center mb-2">
                  <span className="bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-1 rounded mr-2">Farsi</span>
                  <span className="font-semibold">{v.farsi}</span>
                </div>
                <p className="text-gray-600">{v.desc}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4 text-amber-700">Useful Phrases</h3>
            <div className="space-y-3">
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="font-medium md:w-1/3">Do you have any rooms available?</span>
                <span className="text-gray-600 md:w-2/3">Otagh khāli dārid? (اتاق خالی دارید؟)</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="font-medium md:w-1/3">How much is it per night?</span>
                <span className="text-gray-600 md:w-2/3">Shabī chand? (شبی چند؟)</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="font-medium md:w-1/3">Is breakfast included?</span>
                <span className="text-gray-600 md:w-2/3">Sobhāneh dar nazar gerefteh shodeh? (صبحانه در نظر گرفته شده؟)</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="font-medium md:w-1/3">Where is the bathroom?</span>
                <span className="text-gray-600 md:w-2/3">Hammām kojāst? (حمام کجاست؟)</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center">
                <span className="font-medium md:w-1/3">Thank you for your hospitality</span>
                <span className="text-gray-600 md:w-2/3">Moteshakkeram az mehmān navāzi shomā (متشکرم از مهمان نوازی شما)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Final CTA */}
    <section className="py-16 bg-amber-600 text-white">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl font-bold mb-6 text-white">Ready to Experience Persian Hospitality?</h2>
        <p className="text-xl mb-8 max-w-2xl mx-auto">Whether you choose a luxury hotel or a traditional homestay, your stay in Iran will be filled with warmth and unforgettable experiences.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <a href="#hotels" className="bg-white text-amber-700 px-8 py-4 rounded-lg font-semibold hover:bg-amber-50 transition">Browse Hotels</a>
          <a href="#local-hosts" className="border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-amber-700 transition">Find Local Hosts</a>
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-lg font-semibold mb-4">Staying in Iran</h3>
            <p className="text-gray-400">Your guide to accommodation options and cultural experiences throughout Iran.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li><a href="#hotels" className="text-gray-400 hover:text-amber-400 transition">Hotels</a></li>
              <li><a href="#local-hosts" className="text-gray-400 hover:text-amber-400 transition">Local Hosts</a></li>
              <li><button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer">Travel Tips</button></li>
              <li><button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer">Cultural Guide</button></li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <ul className="space-y-2">
              <li className="flex items-center text-gray-400"><FaEnvelope className="mr-2" /> support@pingquack.com</li>
              <li className="flex items-center text-gray-400"><FaPhone className="mr-2" /> +98 21 2345 6789</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              <button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer"><FaInstagram className="text-xl" /></button>
              <button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer"><FaTelegram className="text-xl" /></button>
              <button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer"><FaTwitter className="text-xl" /></button>
              <button className="text-gray-400 hover:text-amber-400 transition bg-transparent border-none cursor-pointer"><FaFacebook className="text-xl" /></button>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2023 Staying in Iran. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
);

export default StayGuide; 